import { useForm } from 'react-hook-form';
import React, { useState } from 'react';

import { formatLinkBlockToUrl } from '$utilities/format';
import { Input, FormError } from '$partials/ui';
import { useLocale } from '$contexts/locale';
import ModuleContainer from '$partials/modules/container.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/global.d.ts').Footer}
 */
const Footer = ({
    column1,
    column2,
    heading,
    socialLinks,
}) => {
    const { translate: T } = useLocale();

    const [newsletterMessage, setNewsletterMessage] = useState();

    const {
        register,
        handleSubmit,
        reset,
        formState: {
            errors
        }
    } = useForm();

    const handleNewsletterSubmit = async (formData) => {
        const response = await fetch('/api/crm/v1/form', {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                formId: '60303f6c-c33d-4b09-8f72-f882759fad9c',
                fields: {
                    email: formData.emailAddress,
                    firstname: formData.firstName,
                    lastname: formData.lastName,
                    title: formData.title,
                },
                context: {
                    pageUri: window ? window.location.hostname + window.location.pathname : undefined,
                    pageName: 'Ion Riva'
                }
            })
        });

        const result = await response.json();

        if (result.status !== 200) {
            setNewsletterMessage(T('[FORM_ERROR_MESSAGE]'));
        } else {
            setNewsletterMessage(T('[NEWSLETTER_THANKYOU]'));
            reset();
        }
    };

    return (
        <footer className='type-style-body'>
            <ModuleContainer heading={heading}>
                <div className='w-full flex flex-col md:gap-30 md:divide-y divide-black'>
                    <div className='w-full grid grid-cols-1 md:grid-cols-2 gap-30 md:gap-8'>
                        <div>
                            <h3 className='mb-20'>
                                {T('[FOOTER_SOCIAL]')}
                            </h3>

                            <div className='grid grid-cols-2 gap-8'>
                                {socialLinks?.map((item, i) => {
                                    return (
                                        <a
                                            className='group/button block bg-stone rounded py-8 px-6 h-[136px]'
                                            href={formatLinkBlockToUrl(item.link)}
                                            key={i}
                                            rel={item.link?.linkTo === 'custom_url' ? 'noreferrer' : undefined}
                                            target={item.link?.linkTo === 'custom_url' ? '_blank' : undefined}>
                                            <span className='block'>
                                                {item.label}
                                            </span>

                                            <span
                                                aria-hidden={true}
                                                className='block mt-20 group-hover/button:translate-x-[4px] transition-transform'>
                                                →
                                            </span>
                                        </a>
                                    );
                                })}
                            </div>
                        </div>

                        <div>
                            <h3 className='mb-20'>
                                {T('[FORM_FIELD_HEADING]')}
                            </h3>

                            <form onSubmit={handleSubmit(handleNewsletterSubmit)}>
                                <div className='w-full flex flex-col gap-8'>
                                    <div className='flex gap-8'>
                                        <div className='w-1/2'>
                                            <div className='rounded bg-stone px-12'>
                                                <Input
                                                    {...register('title', { required: T('[FORM_FIELD_TITLE_IS_REQUIRED]') })}
                                                    id='title'
                                                    label={T('[FORM_FIELD_TITLE]')} />
                                            </div>

                                            {errors.title && (
                                                <FormError error={errors.title} />
                                            )}
                                        </div>
                                    </div>

                                    <div className='flex gap-8'>
                                        <div className='w-1/2'>
                                            <div className='rounded bg-stone px-12'>
                                                <Input
                                                    {...register('firstName', { required: T('[FORM_FIELD_FIRST_NAME_IS_REQUIRED]') })}
                                                    id='firstName'
                                                    label={T('[FORM_FIELD_FIRST_NAME]')} />
                                            </div>

                                            {errors.firstName && (
                                                <FormError error={errors.firstName} />
                                            )}
                                        </div>

                                        <div className='w-1/2'>
                                            <div className='rounded bg-stone px-12'>
                                                <Input
                                                    {...register('lastName', { required: T('[FORM_FIELD_LAST_NAME_IS_REQUIRED]') })}
                                                    id='lastName'
                                                    label={T('[FORM_FIELD_LAST_NAME]')} />
                                            </div>

                                            {errors.lastName && (
                                                <FormError error={errors.lastName} />
                                            )}
                                        </div>
                                    </div>

                                    <div className='w-full'>
                                        <div className='rounded bg-stone px-12'>
                                            <Input
                                                {...register('emailAddress', { required: T('[FORM_FIELD_EMAIL_IS_REQUIRED]') })}
                                                id='emailAddress'
                                                label={T('[FORM_FIELD_EMAIL]')}
                                                type='email' />
                                        </div>

                                        {errors.emailAddress && (
                                            <FormError error={errors.emailAddress} />
                                        )}
                                    </div>

                                    <div className='flex gap-8 justify-end items-center'>
                                        {newsletterMessage &&
                                            <div className='w-1/2 px-12 type-style-tiny'>
                                                {newsletterMessage}
                                            </div>
                                        }

                                        <div className='w-1/2'>
                                            <button
                                                className='group/button flex justify-between items-center bg-purple text-offwhite rounded w-full h-button-height px-12'
                                                type='submit'>
                                                <span>
                                                    {T('[NEWSLETTER_FORM_SUBMIT]')}
                                                </span>
                                                <span className='group-hover/button:translate-x-[4px] transition-transform'>
                                                    →
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>

                    <div className='w-full grid grid-cols-1 md:grid-cols-2 gap-30 md:gap-8 pt-8 pb-60'>
                        {column1 &&
                            <div
                                className='rich-text pt-button-baseline'
                                dangerouslySetInnerHTML={{ __html: column1 }} />
                        }

                        {column2 &&
                            <div
                                className='rich-text pt-button-baseline'
                                dangerouslySetInnerHTML={{ __html: column2 }} />
                        }
                    </div>
                </div>
            </ModuleContainer>
        </footer>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default Footer;
